import { type Context, use } from "react";
import { useSiteConfiguration } from "@/contexts/site-configuration/site-configuration.context";
import type { UserModel } from "@/services/api/profile/models/user.model";
import { userWithoutAccessFallback } from "@/services/api/profile/utilities/user-model-fallback";
import type { SiteConfiguration } from "@/site-configuration/site-configuration.types";
import type { AuthenticationStrategy } from "../authentication-strategy.types";
import { Auth0UserContext } from "../providers/auth0/contexts/auth0-user-context";
import { UserKeycloakAuthenticationContext } from "../providers/keycloak/contexts/keycloak-user-context";
export type UseAuthentication = {
  readonly user: UserModel;
  readonly configuration: AuthenticationStrategy.Configuration;
  readonly urls: UseAuthentication.Urls;
};
export namespace UseAuthentication {
  export type Urls = {
    readonly manageAccount: string;
    readonly createProfile: (redirect: string) => string;
    readonly createTrial: (redirect: string) => string;
    readonly createLogin: (redirect: string) => string;
    readonly createLogout: (redirect: string) => string;
    readonly createLogoutToLogin: (redirect: string) => string;
  };
}
const fallbackAuthentication: UseAuthentication = {
  configuration: {
    hasDoubleOptIn: false,
    hasInternalProfilePages: false,
    isPasswordRequired: false
  },
  urls: {
    createLogin: () => "#",
    createLogout: () => "#",
    createLogoutToLogin: () => "#",
    createProfile: () => "#",
    createTrial: () => "#",
    manageAccount: "#"
  },
  user: userWithoutAccessFallback
};
export function useAuthentication() {
  const {
    features: t0
  } = useSiteConfiguration();
  const {
    loginProvider
  } = t0;
  if (loginProvider === null) {
    return fallbackAuthentication;
  }
  const context = loginProviderContextMap[loginProvider];
  if (!context) {
    return fallbackAuthentication;
  }
  const authentication = use(context);
  return authentication ? authentication : fallbackAuthentication;
}
const loginProviderContextMap: Partial<Record<SiteConfiguration.LoginProviders, Context<UseAuthentication | undefined>>> = {
  auth0: Auth0UserContext,
  keycloak: UserKeycloakAuthenticationContext
};